import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, User, Filter, Search, ChevronDown, ChevronUp, Users, CheckCircle, Eye } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { usePermissions } from '../hooks/usePermissions';
import { FeaturedLayout } from './layouts';
import { Button, Card, CardContent, CardTitle, CardDescription, Badge, Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui';
import { getStaffGroups, getAllGroupsWithStaff, countActiveGroupPlayers, getGroupsWithAttendanceToday } from '../lib/supabase';
import { getMexicoDateString } from '../lib/dateUtils';

const GroupsList = () => {
  const navigate = useNavigate();
  const { user, coach } = useAuth();
  const { isAdmin } = usePermissions();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters and search
  const [searchTerm, setSearchTerm] = useState('');
  const [groupStatusFilter, setGroupStatusFilter] = useState('activos');
  const [selectedStatus, setSelectedStatus] = useState('todos');
  const [selectedRama, setSelectedRama] = useState('todos');
  const [selectedType, setSelectedType] = useState('todos');
  const [showFilters, setShowFilters] = useState(false);


  // Count active filters
  const getActiveFiltersCount = () => {
    let count = 0;
    if (groupStatusFilter !== 'activos') count++;
    if (selectedStatus !== 'todos') count++;
    if (selectedRama !== 'todos') count++;
    if (selectedType !== 'todos') count++;
    return count;
  };

  // Get unique values from groups data for dynamic filters
  const getUniqueValues = (field) => {
    const values = groups
      .map(group => group[field])
      .filter(value => value && value.trim() !== '')
      .filter((value, index, self) => self.indexOf(value) === index)
      .sort();
    return values;
  };

  const uniqueStatus = getUniqueValues('estatus');
  const uniqueRamas = getUniqueValues('rama');
  const uniqueTypes = getUniqueValues('tipo_de_grupo');


  // Filter groups based on search and filters
  const filteredGroups = React.useMemo(() => {
    return groups.filter((group) => {
      const estatusLower = (group.estatus || '').toString().trim().toLowerCase();
      if (groupStatusFilter === 'activos' && estatusLower !== 'activo') {
        return false;
      }
      if (groupStatusFilter === 'inactivos' && estatusLower !== 'inactivo') {
        return false;
      }

      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch =
          (group.nombre || group.nombre_del_grupo || '').toLowerCase().includes(searchLower) ||
          (group.descripcion || '').toLowerCase().includes(searchLower) ||
          (group.rama || '').toLowerCase().includes(searchLower) ||
          group.assignedCoaches?.some((coach) =>
            coach.nombre_completo?.toLowerCase().includes(searchLower)
          );
        if (!matchesSearch) return false;
      }

      if (
        selectedStatus !== 'todos' &&
        estatusLower !== selectedStatus.toString().trim().toLowerCase()
      ) {
        return false;
      }

      if (
        selectedRama !== 'todos' &&
        (group.rama || '').toLowerCase() !== selectedRama.toLowerCase()
      ) {
        return false;
      }

      if (
        selectedType !== 'todos' &&
        (group.tipo_de_grupo || '').toLowerCase() !== selectedType.toLowerCase()
      ) {
        return false;
      }

      return true;
    });
  }, [
    groups,
    groupStatusFilter,
    searchTerm,
    selectedStatus,
    selectedRama,
    selectedType
  ]);

  const loadCoachGroups = useCallback(async () => {
    try {
      setLoading(true);

      if (!isAdmin && !coach?.id) {
        setLoading(false);
        return;
      }

      const { data, error } = isAdmin
        ? await getAllGroupsWithStaff()
        : await getStaffGroups(coach.id);

      if (error) throw error;

      // Extraer grupos y conteos del query principal (sin queries extras)
      const groupsList = (data || []).map((item) => {
        const group = isAdmin ? item : item.grupos;

        let assignedCoaches = [];
        if (isAdmin) {
          const staffRelation = (item.staff_grupos || item.coaches_grupos || [])
            .filter(sg => sg.activo !== false && (sg.staff || sg.coaches));
          assignedCoaches = staffRelation.map(sg => sg.staff || sg.coaches);
        } else if (item.staff || item.coaches) {
          assignedCoaches = [item.staff || item.coaches];
        }

        // Conteos directos del query (ya incluyen jugadores_grupos y series_entrenamiento)
        const playersCount = countActiveGroupPlayers(group.jugadores_grupos || []);
        const seriesCount = (group.series_entrenamiento || []).length;

        return {
          ...group,
          nombre: group.nombre_del_grupo || group.nombre,
          playersCount,
          seriesCount,
          hasTodayAttendance: false, // se actualiza abajo
          assignedCoaches,
          coachName: assignedCoaches[0]?.nombre_completo || null,
          coachEmail: assignedCoaches[0]?.email || null
        };
      });

      // Una sola query batch para saber qué grupos tienen asistencia hoy
      const groupIds = groupsList.map((g) => g.id).filter(Boolean);
      const { data: todaySet } = await getGroupsWithAttendanceToday(groupIds);

      for (const g of groupsList) {
        g.hasTodayAttendance = todaySet.has(g.id);
      }

      setGroups(groupsList);
    } catch (error) {
      console.error('[GroupsList] load error:', error);
    } finally {
      setLoading(false);
    }
  }, [coach, isAdmin]);

  useEffect(() => {
    if (coach || isAdmin) {
      loadCoachGroups();
    }
  }, [coach, isAdmin, loadCoachGroups]);


  const handleEntrenarAhora = (group) => {
    // Navegar a la pantalla de asistencia para el grupo
    // Se detectará automáticamente si hay serie activa
    navigate(`/attendance/${group.id}`, {
      state: {
        group: group,
        mode: 'entrenamiento', // Indica que es modo entrenamiento regular
        fecha: getMexicoDateString() // Fecha actual
      }
    });
  };

  const handleViewPlayers = (group) => {
    navigate(`/players`, { state: { selectedGroup: group } });
  };

  const handleViewGroup = (group) => {
    navigate(`/groups/${group.id}`, { state: { group } });
  };

  const getStatusDotClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'activo': return 'bg-green-500';
      case 'inactivo': return 'bg-yellow-500';
      case 'en preparación': return 'bg-blue-500';
      case 'cerrado': return 'bg-red-500';
      default: return 'bg-muted-foreground';
    }
  };

  return (
    <FeaturedLayout
      title="Mis Grupos"
      subtitle="Gestiona tus equipos"
      refreshAction={loadCoachGroups}
    >
      {/* Toolbar */}
      <div className="mb-4 flex items-center gap-2 flex-nowrap">
        <div className="flex-1 min-w-0 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Buscar grupos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <span className="text-sm text-muted-foreground hidden sm:block">
          <span className="font-medium text-foreground">{filteredGroups.length}</span> grupos
        </span>
        <Button variant="outline" onClick={() => setShowFilters(!showFilters)} className="relative h-10 w-10 p-0 sm:h-10 sm:w-auto sm:px-3">
          <Filter className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Filtros</span>
          {showFilters ? <ChevronUp className="h-4 w-4 ml-2 hidden sm:inline" /> : <ChevronDown className="h-4 w-4 ml-2 hidden sm:inline" />}
        </Button>
      </div>

      {showFilters && (
        <div className="mb-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {/* Estado General Filter */}
          <div>
            <Label className="block text-sm font-medium text-foreground mb-2">
              Estado general
            </Label>
            <Select value={groupStatusFilter} onValueChange={setGroupStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Estado general" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="activos">Solo activos</SelectItem>
                <SelectItem value="inactivos">Inactivos</SelectItem>
                <SelectItem value="todos">Todos</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Status Filter */}
          <div>
            <Label className="block text-sm font-medium text-foreground mb-2">
              Estado
            </Label>
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger>
                <SelectValue placeholder="Todos los estados" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos los estados</SelectItem>
                {uniqueStatus.map(status => (
                  <SelectItem key={status} value={status}>
                    {status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Rama Filter */}
          <div>
            <Label className="block text-sm font-medium text-foreground mb-2">
              Rama
            </Label>
            <Select value={selectedRama} onValueChange={setSelectedRama}>
              <SelectTrigger>
                <SelectValue placeholder="Todas las ramas" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todas las ramas</SelectItem>
                {uniqueRamas.map(rama => (
                  <SelectItem key={rama} value={rama}>
                    {rama}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Type Filter */}
          <div>
            <Label className="block text-sm font-medium text-foreground mb-2">
              Tipo
            </Label>
            <Select value={selectedType} onValueChange={setSelectedType}>
              <SelectTrigger>
                <SelectValue placeholder="Todos los tipos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos los tipos</SelectItem>
                {uniqueTypes.map(tipo => (
                  <SelectItem key={tipo} value={tipo}>
                    {tipo}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          </div>
          {getActiveFiltersCount() > 0 && (
            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={() => { setSelectedStatus('todos'); setSelectedRama('todos'); setSelectedType('todos'); }}>
                Limpiar filtros
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Groups List */}
      {loading ? (
        <p className="text-muted-foreground text-sm py-8 text-center">Cargando grupos...</p>
      ) : filteredGroups.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-30" />
            <p className="text-muted-foreground mb-2">
              {groups.length === 0 ? 'No tienes grupos asignados' : 'No se encontraron grupos con los filtros aplicados'}
            </p>
            {groups.length === 0 && (
              <p className="text-xs text-muted-foreground">
                {isAdmin ? 'Crea un grupo desde Administración > Grupos' : 'Contacta al administrador para que te asigne grupos'}
              </p>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {filteredGroups.map((group) => {
            const meta = [group.rama, group.tipo_de_grupo].filter(Boolean).join(' · ');
            const extraCoaches = (group.assignedCoaches?.length || 0) - 1;
            return (
              <Card
                key={group.id}
                className="h-full transition-all duration-200 hover:shadow-md flex flex-col"
              >
                <CardContent className="p-4 flex flex-col gap-2 flex-grow">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <CardTitle className="text-base font-semibold leading-tight truncate">
                        {group.nombre}
                      </CardTitle>
                      {meta && (
                        <CardDescription className="text-xs mt-0.5 truncate">
                          {meta}
                        </CardDescription>
                      )}
                    </div>
                    <Badge variant="outline" className="shrink-0 gap-1.5 font-normal text-[11px] px-2 py-0.5">
                      <span className={`h-1.5 w-1.5 rounded-full ${getStatusDotClass(group.estatus)}`} />
                      {group.estatus}
                    </Badge>
                  </div>

                  {group.descripcion && (
                    <p className="text-xs text-muted-foreground truncate">
                      {group.descripcion}
                    </p>
                  )}

                  <div className="flex items-center gap-x-3 gap-y-1 flex-wrap text-xs text-muted-foreground mt-auto pt-1">
                    <span className="flex items-center gap-1.5">
                      <Users className="h-3.5 w-3.5 shrink-0" />
                      {group.playersCount} jugadores
                    </span>
                    {isAdmin && (
                      group.coachName ? (
                        <span className="flex items-center gap-1.5 min-w-0">
                          <User className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">{group.coachName}</span>
                          {extraCoaches > 0 && <span className="shrink-0">+{extraCoaches}</span>}
                        </span>
                      ) : (
                        <span className="flex items-center gap-1.5 text-destructive">
                          <User className="h-3.5 w-3.5 shrink-0" />
                          Sin coach
                        </span>
                      )
                    )}
                  </div>

                  <div className="flex gap-2 pt-1">
                    <Button variant="outline" size="sm" onClick={() => handleViewGroup(group)} className="flex-1 h-8">
                      <Eye className="h-3.5 w-3.5 mr-1.5" /> Ver
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleEntrenarAhora(group)}
                      title="Tomar asistencia"
                      className="flex-1 h-8 relative"
                    >
                      <Play className="h-3.5 w-3.5 mr-1.5" />
                      Entrenar
                      {group.hasTodayAttendance && (
                        <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full flex items-center justify-center bg-green-600 text-white">
                          <CheckCircle className="h-3 w-3" />
                        </span>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

    </FeaturedLayout>
  );
};

export default GroupsList;


